import { Component, Element, Prop, h, Listen, State } from '@stencil/core';

@Component({
  tag: 'biel-chat',
  styleUrl: 'biel-chat.css',
  shadow: true,
})
export class BielChat {
  @State() offset: number = 0;
  @State() chatHeight: string = '';
  @State() previousScrollTop: number = 0;
  @State() isUserScrollingUp: boolean = false;

  @Prop() messages: {
    text: string;
    sender: 'user' | 'ai';
    messageId?: string;
    feedback?: 0 | 1;
    sources?: { title: string; url: string }[];
    isPartial: boolean;
    partialId?: number;
  }[] = [];
  @Prop() isFullscreen: boolean;
  @Prop() isLoading: boolean = false;
  @Prop() suggestedQuestions: string[];
  @Prop() welcomeMessage: string;
  @Prop() suggestedQuestionsTitle: string;
  @Prop() loadedChat: boolean;
  @Prop() hideFeedback: boolean;
  @Prop() modalPosition: string;
  @Prop() embeddedMode: boolean = false;
  @Prop() bielId: string;
  @Prop() sourcesText: string = 'Sources';

  @Element() el: HTMLElement;

  private chatContainer: HTMLElement;

  @Listen('textareaHeightChanged', { target: 'body' })
  handleTextareaHeightChanged(event: CustomEvent<number>) {
    this.offset += event.detail;
    this.adjustChatHeight(true);
  }

  @Listen('addMessageText', { target: 'body' })
  handleTextUpdate(event: CustomEvent<{ text: string; id: number; bielId: string }>) {
    if (this.bielId === event.detail.bielId && !this.isUserScrollingUp) {
      this.scrollToBottom();
    }
  }

  @Listen('partialMessageComplete', { target: 'body' })
  handlePartialMessageComplete(event: CustomEvent<{ partialId: number; bielId: string }>) {
    if (this.bielId === event.detail.bielId && !this.isUserScrollingUp) {
      setTimeout(() => {
        this.scrollToBottom();
      }, 100);
    }
  }

  @Listen('scrollToBottom', { target: 'body' })
  handleScrollToBottom(event: CustomEvent<{ bielId: string }>) {
    if (this.bielId === event.detail.bielId) {
      this.scrollToBottom();
    }
  }

  adjustChatHeight(textarea: boolean) {
    requestAnimationFrame(() => {
      const chatContainer = this.el.shadowRoot.querySelector<HTMLElement>('.biel-chat-container');
      if (chatContainer) {
        if (textarea) {
          chatContainer.style.transition = 'all 0s ease-in-out';
        }
        const varHeight = getComputedStyle(document.body).getPropertyValue('--biel-chat-height');
        if (this.modalPosition !== 'sidebar-right' && this.modalPosition !== 'sidebar-left') {
          const baseHeight = this.isFullscreen ? 'calc(100vh - 180px)' : varHeight;
          this.chatHeight = `calc(${baseHeight} - ${this.offset}px)`;
          chatContainer.style.height = this.chatHeight;
        } else {
          this.chatHeight = `calc(100vh - 200px - ${this.offset}px)`;
          chatContainer.style.height = this.chatHeight;
        }
        setTimeout(() => {
          chatContainer.style.transition = 'all 0.3s ease-in-out';
        }, 300);
      }
    });
  }

  componentDidUpdate() {
    this.adjustChatHeight(false);
    if (this.isLoading) {
      this.scrollToBottom();
    }
  }

  scrollToBottom() {
    const container = this.el.shadowRoot.querySelector('.biel-chat-container');
    if (container) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth',
      });
    }
  }

  private onScroll = () => {
    if (this.chatContainer) {
      const { scrollTop, scrollHeight, clientHeight } = this.chatContainer;

      this.isUserScrollingUp = scrollTop < this.previousScrollTop;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 100;
      if (isAtBottom) {
        this.isUserScrollingUp = false;
        if (this.isLoading) {
          this.scrollToBottom();
        }
      }

      this.previousScrollTop = scrollTop;
    }
  };

  componentDidLoad() {
    this.chatContainer = this.el.shadowRoot.querySelector('.biel-chat-container');
    if (this.chatContainer) {
      this.chatContainer.addEventListener('scroll', this.onScroll);
    }
  }

  disconnectedCallback() {
    if (this.chatContainer) {
      this.chatContainer.removeEventListener('scroll', this.onScroll);
    }
  }

  render() {
    return (
      <div
        class={`biel-chat-container ${this.embeddedMode ? 'biel-chat-container--embedded' : ''}  ${this.isFullscreen ? 'fullscreen' : ''} ${this.modalPosition}`}
      >
        {this.welcomeMessage && !this.loadedChat && (
          <biel-message
            text={this.welcomeMessage}
            sender="ai"
            sourcesText={this.sourcesText}
          ></biel-message>
        )}
        {this.suggestedQuestions && this.suggestedQuestions.length > 0 && !this.loadedChat && (
          <biel-suggested-questions
            heading={this.suggestedQuestionsTitle}
            questions={this.suggestedQuestions}
            bielId={this.bielId}
          ></biel-suggested-questions>
        )}
        {this.messages.map((message) => (
          <biel-message
            messageId={message.messageId}
            text={message.text}
            sender={message.sender}
            sources={message.sources}
            feedback={message.feedback}
            hideFeedback={this.hideFeedback}
            bielId={this.bielId}
            isPartial={message.isPartial}
            partialId={message.partialId}
            sourcesText={this.sourcesText}
          ></biel-message>
        ))}
        {this.isLoading && (
          <div class="loading-animation">
            <div class="dot"></div>
            <div class="dot"></div>
            <div class="dot"></div>
          </div>
        )}
      </div>
    );
  }
}
